import React, { useState, useRef, useLayoutEffect } from 'react';
import * as SubframeCore from '@subframe/core';
import { AccountStats, Cluster } from 'api/models';
import { AtomicTooltip } from 'components/design-system';
import { InformationalHeader } from 'subframe/components/InformationalHeader';
import { SkeletonText } from 'subframe/components/SkeletonText';
import { InfoTooltip } from 'subframe/index';
import { getClusterProvider, getClusterRegion } from 'utils/clusterStates';
import { fNumber } from 'utils/formatNumber';
import isExampleCluster from 'utils/isExampleCluster';

interface CountHeaderProps {
  totalClusters: Cluster[];
  isLoadingTotalClusters: boolean;
  showExampleData?: boolean;
  accountStats?: AccountStats;
  isLoadingAccountStats: boolean;
}

function CountHeader({
  totalClusters,
  isLoadingTotalClusters,
  showExampleData,
  accountStats,
  isLoadingAccountStats,
}: CountHeaderProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  let clustersTooltipData: { [key: string]: Cluster[] } = {
    total: [],
  };
  let nodesTooltipData: { [key: string]: number } = {
    total: 0,
  };
  const getClustersTooltipData = () => {
    const x = totalClusters?.map((c: Cluster) => {
      if (showExampleData ? isExampleCluster(c) : !isExampleCluster(c)) {
        const provider = getClusterProvider(c);
        (clustersTooltipData[provider] ??= []).push(c);

        nodesTooltipData[provider] =
          (nodesTooltipData[provider] ?? 0) + c?.node_count;

        clustersTooltipData.total.push(c);
        nodesTooltipData.total += c?.node_count;
      }
    });
  };

  if (!isLoadingTotalClusters) getClustersTooltipData();
  else if (isLoadingTotalClusters) {
    clustersTooltipData = {
      total: [],
    };
    nodesTooltipData = {
      total: 0,
    };
  }

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  useLayoutEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) return;

    const updateArrowsVisibility = () => {
      setShowLeftArrow(containerElement.scrollLeft > 0);
      setShowRightArrow(
        containerElement.scrollLeft + containerElement.clientWidth <
          containerElement.scrollWidth,
      );
    };

    updateArrowsVisibility();

    window.addEventListener('resize', updateArrowsVisibility);
    containerElement.addEventListener('scroll', updateArrowsVisibility);

    const resizeObserver = new ResizeObserver(updateArrowsVisibility);
    resizeObserver.observe(containerElement);

    return () => {
      window.removeEventListener('resize', updateArrowsVisibility);
      containerElement.removeEventListener('scroll', updateArrowsVisibility);
      resizeObserver.disconnect();
    };
  }, [
    totalClusters,
    isLoadingTotalClusters,
    showExampleData,
    accountStats,
    isLoadingAccountStats,
  ]);

  return (
    <div className="flex items-center w-full">
      {showLeftArrow && (
        <SubframeCore.Icon
          className="hover:text-brand-600 cursor-pointer text-body font-body text-default-font ml-2 flex justify-center items-center text-lg font-bold z-10"
          name="FeatherChevronLeft"
          onClick={scrollLeft}
        />
      )}
      <div
        ref={containerRef}
        className="flex-1 whitespace-nowrap scroll-smooth no-scrollbar mx-2"
        style={{ overflowX: 'hidden' }}
      >
        <div className="flex shrink-0 basis-0 items-start gap-6">
          <InformationalHeader className="h-auto w-auto flex-none">
            <InformationalHeader.Item
              title="Clusters"
              iconName={null}
              headingSlot={
                <SubframeCore.HoverCard.Root>
                  <SubframeCore.HoverCard.Trigger asChild={true}>
                    <AtomicTooltip tooltipContent="Total Clusters onboarded by installing Chkk K8s Connector">
                      <SubframeCore.Icon
                        className={
                          'group/58466bc5 text-body font-body text-subtext-color'
                        }
                        name="FeatherInfo"
                      />
                    </AtomicTooltip>
                  </SubframeCore.HoverCard.Trigger>
                </SubframeCore.HoverCard.Root>
              }
              bodySlot={
                <>
                  {showExampleData ? (
                    <span className="text-body-bold font-body-bold text-default-font">
                      {isLoadingTotalClusters ? (
                        <SkeletonText className={'w-5'} />
                      ) : (
                        fNumber(totalClusters.length)
                      )}
                    </span>
                  ) : (
                    <span className="text-body-bold font-body-bold text-default-font">
                      {isLoadingAccountStats ? (
                        <SkeletonText className={'w-5'} />
                      ) : (
                        accountStats &&
                        fNumber(accountStats?.clusters_count ?? 0)
                      )}
                    </span>
                  )}
                </>
              }
            />

            {!isLoadingTotalClusters ? (
              <>
                {Object.keys(clustersTooltipData).map((key) => {
                  return key !== 'total' ? (
                    <InformationalHeader.Item
                      key={key}
                      title={key}
                      iconName={null}
                      headingSlot={<></>}
                      bodySlot={
                        <span className="text-body-bold font-body-bold text-default-font">
                          {`${fNumber(clustersTooltipData[key]?.length ?? 0)}`}
                        </span>
                      }
                    />
                  ) : (
                    <></>
                  );
                })}
              </>
            ) : (
              <></>
            )}
          </InformationalHeader>

          <div className="flex h-12 w-0 flex-none flex-col items-start gap-2 border border-solid border-neutral-border" />

          <InformationalHeader className="h-auto w-auto flex-none">
            <InformationalHeader.Item
              title="Nodes"
              iconName={null}
              headingSlot={
                <AtomicTooltip tooltipContent="Node count from last scan, for clusters onboarded by installing Chkk K8s Connector">
                  <SubframeCore.Icon
                    className={
                      'group/58466bc5 text-body font-body text-subtext-color'
                    }
                    name="FeatherInfo"
                  />
                </AtomicTooltip>
              }
              bodySlot={
                <span className="text-body-bold font-body-bold text-default-font">
                  {showExampleData ? (
                    <span className="text-body-bold font-body-bold text-default-font">
                      {isLoadingTotalClusters ? (
                        <SkeletonText className={'w-5'} />
                      ) : (
                        nodesTooltipData?.total
                      )}
                    </span>
                  ) : (
                    <span className="text-body-bold font-body-bold text-default-font">
                      {isLoadingAccountStats ? (
                        <SkeletonText className={'w-5'} />
                      ) : (
                        accountStats &&
                        fNumber(accountStats?.average_nodes_count ?? 0)
                      )}
                    </span>
                  )}
                </span>
              }
            />
            {!isLoadingTotalClusters && nodesTooltipData.total ? (
              <>
                {Object.keys(clustersTooltipData).map((key) => {
                  return (
                    key !== 'total' && (
                      <InformationalHeader.Item
                        key={key}
                        title={key}
                        iconName={null}
                        headingSlot={<></>}
                        bodySlot={
                          <span className="text-body-bold font-body-bold text-default-font">
                            {fNumber(nodesTooltipData[key])}
                          </span>
                        }
                      />
                    )
                  );
                })}
              </>
            ) : (
              <></>
            )}
          </InformationalHeader>
        </div>
      </div>
      {showRightArrow && (
        <SubframeCore.Icon
          className="text-body font-body text-default-font mr-2 flex justify-center items-center text-lg font-bold z-10 cursor-pointer hover:text-brand-600"
          name="FeatherChevronRight"
          onClick={scrollRight}
        />
      )}
    </div>
  );
}

export default CountHeader;
